import { AdminWaiver } from "@/constants";
import { Card } from "../ui/card";
import { useState } from "react";
import { CalendarDaysIcon, CircleDollarSign, Loader2, MailIcon, PhoneCallIcon, Eye } from "lucide-react";
import { formatDate, formatShortCryptoAddress } from "@/utils/format";
import { apiFetch } from "@/utils";
import { Button } from "../ui/button";
import { useToast } from "@/hooks/use-toast";
import { downloadBlob } from "@/utils/download";

const AdminWaiverCard: React.FC<{
	groupedMembership: any
}> = ({ groupedMembership }) => {
	const { toast } = useToast();

	const [isDownloadingPdf, setIsDownloadingPdf] = useState<string | null>(null);

	const downloadWaiverPdf = async (waiver: AdminWaiver) => {
		setIsDownloadingPdf(waiver.type);

		try {
			const fullUrl = '/v1' + waiver.pdfUri;

			const response = await apiFetch(fullUrl, {
				method: 'GET',
				withCredentials: true,
			});

			const pdfBlob = await response.blob();
			const pdfFileName = `waiver-${waiver.type}-${waiver.id}.pdf`;

			downloadBlob(pdfBlob, pdfFileName);
		} catch(err: any) {
			toast({
				title: 'PDF Generation Failed',
				description: err.message,
				variant: 'destructive'
			})
		}

		setIsDownloadingPdf(null);
	}

	return <Card className="p-4 flex flex-col sm:flex-row justify-between items-center">
		<div className="flex flex-col gap-1">
			<div className="flex flex-col gap-0 mb-4">
				<h3 className="text-lg font-bold">
					{groupedMembership.fullName}
				</h3>
				<span className="text-md text-primary">
					Membership ID: {groupedMembership.membership.id}
				</span>
			</div>

			<a href={`tel:${groupedMembership.phoneNumber}`} className="text-sm cursor-pointer flex flex-row gap-1 items-center hover:underline">
				<PhoneCallIcon size={16}/>
				{groupedMembership.phoneNumber}
			</a>
			<a href={`mailto:${groupedMembership.emailAddress}`} className="text-sm cursor-pointer flex flex-row gap-1 items-center hover:underline">
				<MailIcon size={16}/>
				{groupedMembership.emailAddress}
			</a>
			<div className="text-sm cursor-pointer flex flex-row gap-1 items-center">
				<CalendarDaysIcon size={16}/>
				{formatDate(groupedMembership.membership.startDate)} - {formatDate(groupedMembership.membership.endDate)}

				{groupedMembership.membership.status === 'active' && <span className="text-green-500 font-bold">(Active)</span>}
				{groupedMembership.membership.status !== 'active' && <span className="text-red-500 font-bold">(Expired)</span>}
			</div>
			<a href="#" className="text-sm cursor-pointer flex flex-row gap-1 items-center hover:underline">
				<CircleDollarSign size={16}/>
				{formatShortCryptoAddress(groupedMembership.customerAddress!)}
			</a>
		</div>

		<div className="flex w-100 sm:w-auto mt-4 sm:mt-0 justify-between gap-1 sm:flex-col">
			{groupedMembership.waivers.club_swinkster && (
				<Button
					onClick={() => downloadWaiverPdf(groupedMembership.waivers.club_swinkster)}
					disabled={isDownloadingPdf === 'club_swinkster'}
				>
					{isDownloadingPdf === 'club_swinkster' ? <Loader2 className="mr-2 h-4 w-4 animate-spin"/> : <Eye className="mr-2 h-4 w-4"/>}
					View Club Swinkster
				</Button>
			)}

			{groupedMembership.waivers.secrets_hideaway && (
				<Button
					onClick={() => downloadWaiverPdf(groupedMembership.waivers.secrets_hideaway)}
					disabled={isDownloadingPdf === 'secrets_hideaway'}
				>
					{isDownloadingPdf === 'secrets_hideaway' ? <Loader2 className="mr-2 h-4 w-4 animate-spin"/> : <Eye className="mr-2 h-4 w-4"/>}
					View Secrets Hideaway
				</Button>
			)}
		</div>
	</Card>
}

export default AdminWaiverCard;